import type {
  LanguageServiceProvider,
  LanguageServiceCompletionResult,
  LanguageServicePosition,
  LanguageServiceConnectionInfo,
} from './languageServiceProvider';
import {
  bumpKustoLoadSeq,
  emitKustoStatus,
  isDocumentOn,
  kustoDocuments,
  kustoStatusListeners,
  loadClusterSchema,
} from './kustoSchemaLoad';

export { reloadKustoSchema, loadedKustoDatabases } from './kustoSchemaLoad';

/**
 * Feeds monaco-kusto's worker the connected cluster's schema so its native
 * completion/hover/diagnostics providers become schema-aware. Plumbing
 * (document map, status listeners) mirrors `sqliteLanguageServiceProvider.ts`
 * verbatim — monaco-kusto ships its own completion/hover/signature-help
 * providers (registered by `ensureKustoLanguageLoaded`'s AMD contribution),
 * so this provider's `getCompletionItems`/`getHoverInfo`/`getSignatureHelp`
 * are no-ops; its only job is the schema feed + IntelliSense status. The
 * registry and the schema load itself live in `kustoSchemaLoad.ts`, shared
 * with the manual "Refresh IntelliSense" path.
 */
export const kustoLanguageServiceProvider: LanguageServiceProvider = {
  engine: 'kusto',
  ensureInitialized: async () => {},

  onIntelliSenseStatus: (listener) => {
    kustoStatusListeners.push(listener);
    return () => {
      const idx = kustoStatusListeners.indexOf(listener);
      if (idx >= 0) kustoStatusListeners.splice(idx, 1);
    };
  },

  getIntelliSenseStatus: (uri) => kustoDocuments.get(uri)?.status ?? 'unavailable',

  registerDocument: async (uri: string, text: string) => {
    kustoDocuments.set(uri, { text, status: 'unavailable' });
    return true;
  },

  updateDocument: async (uri: string, text: string) => {
    const existing = kustoDocuments.get(uri);
    if (existing) {
      existing.text = text;
    } else {
      kustoDocuments.set(uri, { text, status: 'unavailable' });
    }
    return true;
  },

  closeDocument: async (uri: string) => {
    kustoDocuments.delete(uri);
    return true;
  },

  connectForLanguageServices: async (
    uri: string,
    connectionInfo: LanguageServiceConnectionInfo,
  ): Promise<boolean> => {
    const doc = kustoDocuments.get(uri) ?? { text: '', status: 'unavailable' };
    doc.connectionId = connectionInfo.connectionId;
    doc.database = connectionInfo.database;
    doc.host = connectionInfo.host;
    kustoDocuments.set(uri, doc);
    const outcome = await loadClusterSchema(uri, connectionInfo.connectionId, connectionInfo.database, connectionInfo.host);
    if (outcome === 'loaded') return true;
    // `'stale'` while the document is STILL on this pair means a newer load
    // (a manual "Refresh IntelliSense" during the connect-time load)
    // superseded this one and now owns the worker and the status: the
    // document is connected — reporting `false` would make SqlMonaco skip
    // registering it with the sync manager for the rest of its life.
    // `'stale'` after a disconnect / close, and `'failed'`, stay `false` —
    // and so does `'stale'` when the newer load that superseded this one has
    // ALREADY failed by the time this one settles (status `error`): reporting
    // success then would let this abandoned attempt cancel the sync
    // manager's retry of the successor. A superseder still loading at that
    // moment reads as connected; if it fails afterwards, its own `false`
    // schedules the retry.
    return outcome === 'stale'
      && isDocumentOn(uri, connectionInfo)
      && kustoDocuments.get(uri)?.status !== 'error';
  },

  disconnectForLanguageServices: async (uri: string) => {
    const doc = kustoDocuments.get(uri);
    if (doc) {
      doc.connectionId = undefined;
      doc.database = undefined;
      doc.host = undefined;
      // Any load still in flight for this document must not land after this.
      bumpKustoLoadSeq(doc);
      emitKustoStatus(uri, 'unavailable');
    }
    return true;
  },

  // monaco-kusto's own AMD-registered providers supply completions/hover —
  // this provider only feeds the worker's schema (above).
  getCompletionItems: async (): Promise<LanguageServiceCompletionResult> => ({ items: [] }),
  getHoverInfo: async (_uri: string, _position: LanguageServicePosition) => null,
  getSignatureHelp: async (_uri: string, _position: LanguageServicePosition) => null,
};
